import { findMetatag } from 'owa-metatags';
import {
    DEFAULT_LANGUAGE,
    LANGUAGE_TAGS_MAX_NUMBER,
    marketingLandingPageMapping,
    DEFAULT_MARKETING_LANDING_PAGE_URL_TEMPLATE,
} from './constants';
import { getHostLocation } from 'owa-url/lib/hostLocation';

/**
 * Gets the marketing landing page URL template from configuration or returns default
 * Matches server-side pattern: appResourceSettings.CurrentValue.MarketingLandingPageUrl
 */
function getMarketingLandingPageUrlTemplate(): string {
    // Check for configuration in window data (similar to server-side appResourceSettings)
    try {
        // Try to get the URL template from a meta tag first
        const mlpurl = findMetatag('mlpUrl');
        if (mlpurl) {
            return mlpurl;
        }
    } catch {
        // Fallback to default if configuration access fails
    }

    return DEFAULT_MARKETING_LANDING_PAGE_URL_TEMPLATE;
}

/**
 * Validates language tag according to RFC 2616 (simplified version of server-side logic)
 */
function validateLanguageTag(tagWithHeaderValue: string): string | null {
    if (!tagWithHeaderValue) {
        return null;
    }

    const tag = tagWithHeaderValue.trim();

    // Basic validation - allow letters, numbers, and hyphens
    // Stop at semicolon (quality value) or whitespace
    const match = tag.match(/^[a-zA-Z0-9-]+/);
    if (!match) {
        return null;
    }

    return match[0].toLowerCase();
}

/**
 * Gets marketing supported language tag with fallback logic
 */
function getMarketingSupportedLanguageTag(language: string): string | null {
    if (!language) {
        return null;
    }

    let currentLanguage = language.toLowerCase();

    do {
        // Check if we have a direct mapping
        if (marketingLandingPageMapping[currentLanguage]) {
            return marketingLandingPageMapping[currentLanguage];
        }

        // Remove the rightmost part after hyphen for fallback
        const lastHyphenIndex = currentLanguage.lastIndexOf('-');
        if (lastHyphenIndex <= 0) {
            return null; // No more parent languages
        }

        currentLanguage = currentLanguage.substring(0, lastHyphenIndex);
    } while (currentLanguage);

    return null;
}

/**
 * Gets browser supported language from Accept-Language header
 */
function getBrowserSupportedLanguage(userLanguages: string[]): string {
    if (userLanguages && userLanguages.length > 0) {
        // Limit to max number of language tags to process
        const maxIndex = Math.min(LANGUAGE_TAGS_MAX_NUMBER, userLanguages.length);

        for (let i = 0; i < maxIndex; i++) {
            const userLanguage = validateLanguageTag(userLanguages[i]);
            if (userLanguage) {
                const supportedLanguage = getMarketingSupportedLanguageTag(userLanguage);
                if (supportedLanguage) {
                    return supportedLanguage;
                }
            }
        }
    }

    return DEFAULT_LANGUAGE;
}

/**
 * Gets browser accepted languages from navigator.languages or Accept-Language header
 */
function getBrowserAcceptedLanguages(): string[] {
    // Try to get from navigator.languages first (more accurate)
    if (navigator.languages && navigator.languages.length > 0) {
        return Array.from(navigator.languages);
    }

    // Fallback to navigator.language
    if (navigator.language) {
        return [navigator.language];
    }

    return [DEFAULT_LANGUAGE]; // Ultimate fallback
}

/**
 * Build localized marketing page URL based on current user locale
 * (matches server-side logic from LandingPageUtils.cs)
 */
export function getLocalizedMarketingPageUrl(sid?: string): string {
    const location = getHostLocation();
    // Get browser accepted languages
    const userLanguages = getBrowserAcceptedLanguages();

    // Get the supported marketing language
    const language = getBrowserSupportedLanguage(userLanguages);

    // Get the configurable URL template
    const urlTemplate = getMarketingLandingPageUrlTemplate();

    // Build the marketing URL using the configurable template
    const redirectTo = encodeURIComponent(location.pathname + location.search);

    // Determine SDF parameter (matches server-side logic)
    const isSdfV2 = location.hostname.toLowerCase().includes('sdf');
    const sdfParam = isSdfV2 ? '1' : '0';

    let url = urlTemplate
        .replace('{0}', language)
        .replace('{1}', redirectTo)
        .replace('{2}', sdfParam);

    if (sid) {
        url += '&sessionId=' + encodeURIComponent(sid);
    }

    return url;
}
